import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';
import SectionContainer from '../../containers/PageBuilder/SectionBuilder/SectionContainer';
import { NamedLink } from '../../components';

import StepList from '../SectionSteps/StepList';
import { PROOF_POINTS } from '../benefits';
import { PLANS, BASIS, PROFESSIONAL, PREMIUM, VISIBILITY_DELAY_HOURS } from '../membership';
import { DEALER_FEATURES, DEALER_STEPS, PLAN_NOTES, PRICE_NOTE } from '../dealerPage';
import gem from '../salesPage.module.css';
import css from './SectionDealer.module.css';

const PLAN_ORDER = [BASIS, PROFESSIONAL, PREMIUM];

const angebote = plan =>
  plan.offersPerMonth === null ? 'unbegrenzt' : `${plan.offersPerMonth} pro Monat`;

/**
 * Wie lange wartet dieser Tarif auf neue Anfragen? Die Stunden stehen in
 * membership.js - dieselbe Quelle, aus der auch die Startseite und die
 * Tarifseite rechnen. So kann die Werbung nicht vom Produkt abweichen.
 */
const vorsprung = key => {
  const stunden = VISIBILITY_DELAY_HOURS[key];
  return stunden === 0 ? 'sofort sichtbar' : `sichtbar nach ${stunden} Stunden`;
};

const Check = () => (
  <svg
    className={gem.check}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="9" cy="9" r="9" className={gem.checkDisc} />
    <path
      d="M5 9.2 7.6 11.8 13 6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * FairwayBid: Die Händlerseite.
 *
 * Händler sind die zahlende Seite. Die Seite argumentiert deshalb in dieser
 * Reihenfolge: Erst der Beleg, dass hier Nachfrage ist. Dann, was ein
 * Händler davon hat. Dann, wie wenig Aufwand es macht. Erst zum Schluss der
 * Preis - wer bis dahin gelesen hat, fragt nach dem Preis und nicht mehr
 * nach dem Grund.
 *
 * Überschrift und Einleitung kommen aus der Console. Die Argumente stehen in
 * src/fairwaybid/dealerPage.js, die Tarifzahlen in membership.js - dieselbe
 * Quelle wie die Tarifseite, damit Werbung und Produkt nicht auseinander
 * laufen.
 */
const SectionDealer = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={gem.header}>
          <span className={gem.eyebrow}>Für Händler und Hersteller</span>
          <Field data={title} className={gem.headTitle} options={fieldOptions} />
          <span className={gem.rule} aria-hidden="true" />
          <Field data={description} className={gem.intro} options={fieldOptions} />
          <NamedLink name="SignupPage" className={gem.heroCta}>
            Jetzt registrieren
          </NamedLink>
        </header>
      ) : null}

      <div className={gem.wrap}>
        {/* Erst der Beleg: Gibt es hier ueberhaupt Nachfrage? */}
        <dl className={gem.proof}>
          {PROOF_POINTS.map(punkt => (
            <div key={punkt.label} className={gem.proofItem}>
              <dt className={gem.proofValue}>{punkt.value}</dt>
              <dd className={gem.proofLabel}>
                {punkt.label}
                <span className={gem.proofNote}>{punkt.note}</span>
              </dd>
            </div>
          ))}
        </dl>

        <h3 className={gem.blockTitle}>Was Sie davon haben</h3>
        <div className={gem.features}>
          {DEALER_FEATURES.map(f => (
            <article key={f.key} className={gem.feature}>
              <Check />
              <div>
                <h4 className={gem.featureHeadline}>{f.headline}</h4>
                <p className={gem.featureText}>{f.text}</p>
              </div>
            </article>
          ))}
        </div>

        <h3 className={gem.blockTitle}>So läuft es für Sie</h3>
        <StepList steps={DEALER_STEPS} className={gem.steps} />

        <h3 className={gem.blockTitle}>Was es kostet</h3>
        <div className={css.plans}>
          {PLAN_ORDER.map(key => {
            const plan = PLANS[key];
            const hervor = key === PROFESSIONAL;
            return (
              <article key={key} className={classNames(css.plan, { [css.planFeatured]: hervor })}>
                {hervor ? <span className={css.planFlag}>Empfohlen</span> : null}
                <h4 className={css.planName}>{plan.label}</h4>
                <p className={css.planPrice}>
                  {plan.price}
                  <span className={css.planUnit}> netto / Monat</span>
                </p>
                <p className={css.planNote}>{PLAN_NOTES[key]}</p>
                <ul className={css.planList}>
                  <li>Angebote: {angebote(plan)}</li>
                  <li>Neue Anfragen: {vorsprung(key)}</li>
                  <li>
                    {plan.badge ? 'Kennzeichnung im Angebotsvergleich' : 'Ohne Kennzeichnung'}
                  </li>
                </ul>
                <NamedLink
                  name="SignupPage"
                  className={classNames(css.planCta, { [css.planCtaFeatured]: hervor })}
                >
                  {key === BASIS ? 'Kostenlos starten' : `Mit ${plan.label} starten`}
                </NamedLink>
              </article>
            );
          })}
        </div>

        <p className={css.priceNote}>{PRICE_NOTE}</p>
      </div>
    </SectionContainer>
  );
};

export default SectionDealer;
